import { useEffect, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import { Data } from 'react-csv/components/CommonPropTypes'
import { Icon } from '@iconify/react'

import { Keyword } from 'types/keyword'
import { Topic } from '@pepper/utils/src/types/seo'

import { getKeywordsExportProps, getTopicsExportProps } from './utils'
import { useProjectTopics } from 'utils/hooks/useProjectTopics'
import { useProjectKeywords } from 'utils/hooks/useProjectKeywords'

import { Button, Dropdown, Menu, MenuItem, useShowMessage } from '@pepper/ui'
import styles from './ExportButton.module.css'

interface Props {
  title: string
  isSelection: boolean
  selectedTopics?: Topic[]
  selectedKeywords?: Keyword[]
  type: 'topic' | 'keyword'
  projectId: string
  companyId: string
}

type ExportKey = 'ALL' | 'SELECTION'

const EXPORT_ALL = 'ALL'
const EXPORT_SELECTION = 'SELECTION'

const dropdownOverlay = (onExportHandler: (key: ExportKey) => void) => {
  return (
    <Menu
      className={styles.menuContainer}
      onClick={event => onExportHandler(event.key as ExportKey)}
    >
      <MenuItem key={EXPORT_ALL} className={styles.menuItem}>
        <span>Export All</span>
      </MenuItem>
      <MenuItem key={EXPORT_SELECTION} className={styles.menuItem}>
        <span>Export selected</span>
      </MenuItem>
    </Menu>
  )
}

export const ExportButton = ({
  title,
  isSelection,
  selectedTopics,
  selectedKeywords,
  type,
  projectId,
  companyId,
}: Props) => {
  const showMessage = useShowMessage()
  const [isExportClick, setIsExportClick] = useState(false)
  const [csvData, setCsvData] = useState<{
    data: Data
    headers: { label: string; key: string }[]
  } | null>(null)

  const csvLinkRef = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null)

  const { refetch: refetchTopics } = useProjectTopics({
    projectId,
    companyId,
    enabled: false,
  })
  const { refetch: refetchKeywords } = useProjectKeywords({
    projectId,
    companyId,
    enabled: false,
  })

  const updateCsvData = (
    data: Topic[] | Keyword[] | undefined,
    _type: 'topic' | 'keyword'
  ) => {
    if (data) {
      switch (_type) {
        case 'topic':
          setCsvData(getTopicsExportProps(data as Topic[]))
          break
        case 'keyword':
          setCsvData(getKeywordsExportProps(data as Keyword[]))
          break
        default:
          break
      }
    }
  }

  const handleExportAll = async () => {
    setCsvData(null)
    setIsExportClick(true)

    try {
      if (type === 'topic') {
        const result = await refetchTopics()
        updateCsvData(result.data?.records, 'topic')
      } else {
        const result = await refetchKeywords()
        updateCsvData(result.data?.records, 'keyword')
      }
    } catch (error) {
      showMessage({ type: 'error', message: error })
    }
  }

  const onExportHandler = (key: ExportKey) => {
    if (key === EXPORT_SELECTION) {
      setIsExportClick(true)
      switch (type) {
        case 'topic':
          setCsvData(getTopicsExportProps(selectedTopics ?? []))
          break
        case 'keyword':
          setCsvData(getKeywordsExportProps(selectedKeywords ?? []))
          break
        default:
          break
      }
      return
    }

    if (key === EXPORT_ALL) {
      handleExportAll()
    }
  }

  // to manually download as we are not using in-built done() function
  useEffect(() => {
    if (csvData?.data) {
      if (isExportClick && csvData?.data.length > 0) {
        csvLinkRef.current?.link.click()
        setIsExportClick(false)
        setCsvData(null)
      }
    }
  }, [csvData?.data, isExportClick])

  return (
    <div>
      {isSelection ? (
        <Dropdown
          trigger={['click']}
          overlay={dropdownOverlay(onExportHandler)}
        >
          <Button
            type="default"
            aria-label="Download Dropdown"
            icon={<Icon icon="lucide:chevron-down" />}
            iconPlacement="right"
          >
            {title}
          </Button>
        </Dropdown>
      ) : (
        <Button type="default" onClick={() => onExportHandler(EXPORT_ALL)}>
          {title}
        </Button>
      )}
      <CSVLink
        data={csvData?.data || []}
        headers={csvData?.headers || []}
        filename={type === 'topic' ? 'topics.csv' : 'keywords.csv'}
        target="_blank"
        ref={csvLinkRef}
      />
    </div>
  )
}
